// One family on the Holiday Club page: who, what they booked, the money and one-click actions.

import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PercentIcon from "@mui/icons-material/Percent";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import UndoIcon from "@mui/icons-material/Undo";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { formatPounds } from "../../shared/holidayClub";
import { Family, STATUS_COLOR, STATUS_LABEL } from "./families";

export interface FamilyActions {
  setPaid: (family: Family, paid: boolean) => void;
  setCancelled: (family: Family, cancelled: boolean) => void;
  editDiscount: (family: Family) => void;
  receipt: (family: Family) => void;
  recreate: (family: Family) => void;
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]!.toUpperCase()).join("") || "?";

const shortDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const DayChips: React.FC<{ family: Family }> = ({ family }) => (
  <Stack spacing={1.5} sx={{ pt: 1 }}>
    {family.children.map((child) => (
      <Box key={child.registrationId}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{child.name}</Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {child.days.map((day) => (
            <Chip
              key={day.label}
              size="small"
              label={`${day.label} · ${day.choice === "FULL" ? "Full" : day.choice === "HALF" ? "Half" : "—"}`}
              color={day.choice === "FULL" ? "primary" : day.choice === "HALF" ? "secondary" : "default"}
              variant={day.choice === "NONE" ? "outlined" : "filled"}
              sx={day.choice === "NONE" ? { opacity: 0.5 } : undefined}
            />
          ))}
        </Stack>
      </Box>
    ))}
  </Stack>
);

export const FamilyCard: React.FC<{ family: Family; busy: boolean; actions: FamilyActions }> = ({ family, busy, actions }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { invoice, status } = family;
  const cancelled = status === "cancelled";
  const color = STATUS_COLOR[status];
  const accent = color === "default" ? theme.palette.text.disabled : theme.palette[color].main;

  const closeMenu = () => setMenuAnchor(null);
  const menuItem = (label: string, icon: React.ReactNode, onClick: () => void) => (
    <MenuItem key={label} onClick={() => { closeMenu(); onClick(); }}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </MenuItem>
  );

  const menuItems: React.ReactNode[] = [];
  if (invoice && !cancelled) {
    menuItems.push(menuItem(invoice.receipt ? "Create receipt again" : "Create receipt", <ReceiptLongIcon fontSize="small" />, () => actions.receipt(family)));
    menuItems.push(menuItem("Recreate invoice and draft", <AutorenewIcon fontSize="small" />, () => actions.recreate(family)));
    if (invoice.paid) menuItems.push(menuItem("Mark as unpaid", <UndoIcon fontSize="small" />, () => actions.setPaid(family, false)));
  }
  if (!invoice && !cancelled) menuItems.push(menuItem("Discount", <PercentIcon fontSize="small" />, () => actions.editDiscount(family)));
  menuItems.push(cancelled
    ? menuItem("Restore", <RestoreIcon fontSize="small" />, () => actions.setCancelled(family, false))
    : menuItem(invoice ? "Cancel invoice" : "Cancel booking", <BlockIcon fontSize="small" />, () => actions.setCancelled(family, true)));

  const days = [family.fullDays && `${family.fullDays} full`, family.halfDays && `${family.halfDays} half`].filter(Boolean).join(" · ") || "No days";

  return (
    <Paper
      variant="outlined"
      sx={{
        borderLeft: `4px solid ${accent}`,
        opacity: cancelled ? 0.6 : 1,
        transition: "box-shadow 150ms, opacity 150ms",
        "&:hover": { boxShadow: theme.shadows[2] },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        spacing={{ xs: 1.5, md: 2 }}
        sx={{ px: 2, py: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => setOpen(!open)}>
          <Box sx={{ width: 64, flexShrink: 0 }}>
            <Typography fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums", textDecoration: cancelled ? "line-through" : "none" }}
              color={invoice ? "text.primary" : "text.disabled"}>
              {invoice ? `#${invoice.invoiceNo}` : "—"}
            </Typography>
            {invoice?.issuedDate && <Typography variant="caption" color="text.secondary">{shortDate(invoice.issuedDate)}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: alpha(accent, 0.15), color: accent, fontWeight: 600, width: 40, height: 40 }}>
            {initials(family.parentName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={600} noWrap sx={{ textDecoration: cancelled ? "line-through" : "none" }}>
              {family.parentName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {family.children.map((c) => c.name).join(", ")} · {days}
            </Typography>
            {family.problem && (
              <Typography variant="body2" color="warning.dark" sx={{ whiteSpace: "normal" }}>{family.problem}</Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {family.discountPercentage > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`${family.discountReason} ${family.discountPercentage}%` +
                (family.discountPence !== null ? ` · −£${formatPounds(family.discountPence)}` : "")}
              onClick={!invoice && !cancelled ? () => actions.editDiscount(family) : undefined}
            />
          )}
          {invoice?.draftError && (
            <Tooltip title={`Gmail draft failed: ${invoice.draftError}`}>
              <ErrorOutlineIcon color="error" fontSize="small" />
            </Tooltip>
          )}
          <Chip
            size="small"
            color={color}
            icon={status === "attention" ? <WarningAmberIcon /> : undefined}
            label={STATUS_LABEL[status]}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end" sx={{ minWidth: { md: 280 } }}>
          <Typography variant="h6" sx={{ minWidth: 90, textAlign: "right", fontVariantNumeric: "tabular-nums", flex: { xs: 1, md: "none" } }}>
            {family.totalPence !== null ? `£${formatPounds(family.totalPence)}` : "—"}
          </Typography>

          <Box sx={{ minWidth: 130, display: "flex", justifyContent: "flex-end" }}>
            {status === "unpaid" && (
              <Button size="small" variant="outlined" color="success" disabled={busy} startIcon={<CheckCircleIcon />}
                onClick={() => actions.setPaid(family, true)}>
                Mark paid
              </Button>
            )}
            {status === "paid" && (
              <Tooltip title={invoice?.receipt ? `Receipt drafted for payment on ${shortDate(invoice.receipt.paymentDate)}` : "Create receipt"}>
                <Button size="small" variant="outlined" color="success" disabled={busy} startIcon={invoice?.receipt ? <CheckCircleIcon /> : <ReceiptLongIcon />}
                  onClick={() => actions.receipt(family)}>
                  {invoice?.paidDate ? `Paid ${shortDate(invoice.paidDate)}` : "Paid"}
                </Button>
              </Tooltip>
            )}
            {status === "attention" && (family.problem?.startsWith("Discount") ? (
              <Button size="small" variant="outlined" color="warning" disabled={busy} startIcon={<PercentIcon />}
                onClick={() => actions.editDiscount(family)}>
                Fix discount
              </Button>
            ) : (
              <Button size="small" variant="outlined" color="warning" disabled={busy} startIcon={<BlockIcon />}
                onClick={() => actions.setCancelled(family, true)}>
                Cancel booking
              </Button>
            ))}
            {status === "toInvoice" && (
              <Button size="small" variant="outlined" disabled={busy} startIcon={<PercentIcon />} onClick={() => actions.editDiscount(family)}>
                Discount
              </Button>
            )}
          </Box>

          <IconButton size="small" disabled={busy} onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions">
            <MoreVertIcon />
          </IconButton>
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label="Show days"
            sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }}>
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Divider />
        <Box sx={{ px: 2, pb: 2 }}>
          <DayChips family={family} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {family.email}{family.phone ? ` · ${family.phone}` : ""}
            {invoice?.issuedDate ? ` · invoiced ${shortDate(invoice.issuedDate)}` : ""}
          </Typography>
        </Box>
      </Collapse>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>{menuItems}</Menu>
    </Paper>
  );
};
